import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = ['property-articles-count'];

interface PropertyArticlesCount {
  propertyId: string;
  count: number;
}

export function usePropertyArticlesCount(propertyIds: string[]) {
  const query = useQuery({
    queryKey: [...QUERY_KEY, propertyIds.sort().join(',')],
    enabled: propertyIds.length > 0,
    queryFn: async (): Promise<PropertyArticlesCount[]> => {
      const { data, error } = await supabase
        .from('property_kit_entries')
        .select('property_id')
        .in('property_id', propertyIds);

      if (error) throw error;

      // Count entries per property
      const counts: Record<string, number> = {};
      propertyIds.forEach(id => { counts[id] = 0; });
      
      data.forEach((entry) => {
        counts[entry.property_id] = (counts[entry.property_id] || 0) + 1;
      });

      return Object.entries(counts).map(([propertyId, count]) => ({
        propertyId,
        count,
      }));
    },
  });

  const getCount = (propertyId: string): number => {
    return query.data?.find(c => c.propertyId === propertyId)?.count ?? 0;
  };

  return {
    counts: query.data ?? [],
    getCount,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// Get count for a single property
export function useSinglePropertyArticlesCount(propertyId: string | undefined) {
  const query = useQuery({
    queryKey: [...QUERY_KEY, 'single', propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('property_kit_entries')
        .select('*', { count: 'exact', head: true })
        .eq('property_id', propertyId);

      if (error) throw error;
      return count ?? 0;
    },
  });

  return {
    count: query.data ?? 0,
    isLoading: query.isLoading,
    error: query.error,
  };
}
